/**
 * LeadsPageContext - Contexto compartido entre AI y LeadsList
 * Permite que el AI controle la UI de leads mediante comandos
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type {
  AILeadCommand,
  AICommandResult,
  LeadsPageAIContext,
} from '@/types/ai/commands.types';
import type {
  LeadFilterState,
  LeadsListView,
  LeadsListLayout,
} from '@/modules/leads/components/LeadsList/LeadsList';
import type { Lead } from '@/types/lead';

interface LeadsPageContextValue {
  // Current state
  filters: LeadFilterState;
  selectedLeads: Set<string>;
  visibleLeads: Lead[];
  currentView: LeadsListView;
  currentLayout: LeadsListLayout;
  totalLeads: number;
  viewMode: 'list' | 'radar';

  // State setters
  setFilters: (filters: LeadFilterState) => void;
  setSelectedLeads: (leads: Set<string>) => void;
  setVisibleLeads: (leads: Lead[]) => void;
  setCurrentView: (view: LeadsListView) => void;
  setCurrentLayout: (layout: LeadsListLayout) => void;
  setTotalLeads: (count: number) => void;
  setViewMode: (mode: 'list' | 'radar') => void;

  // Command executor
  executeCommand: (command: AILeadCommand) => Promise<AICommandResult>;

  // Context for AI
  getAIContext: () => LeadsPageAIContext;
}

const LeadsPageContext = createContext<LeadsPageContextValue | null>(null);

export const useLeadsPage = () => {
  const context = useContext(LeadsPageContext);
  if (!context) {
    throw new Error('useLeadsPage must be used within LeadsPageProvider');
  }
  return context;
};

interface LeadsPageProviderProps {
  children: React.ReactNode;
  initialFilters?: LeadFilterState;
  initialView?: LeadsListView;
  initialLayout?: LeadsListLayout;
}

export const LeadsPageProvider: React.FC<LeadsPageProviderProps> = ({
  children,
  initialFilters = {},
  initialView = 'all',
  initialLayout = 'grid',
}) => {
  const [filters, setFilters] = useState<LeadFilterState>(initialFilters);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [visibleLeads, setVisibleLeads] = useState<Lead[]>([]);
  const [currentView, setCurrentView] = useState<LeadsListView>(initialView);
  const [currentLayout, setCurrentLayout] = useState<LeadsListLayout>(initialLayout);
  const [totalLeads, setTotalLeads] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'radar'>('list');

  // Get current context for AI
  const getAIContext = useCallback((): LeadsPageAIContext => {
    return {
      totalLeads,
      visibleLeads: visibleLeads.length,
      selectedCount: selectedLeads.size,
      currentFilters: filters,
      currentView,
      currentLayout,
    };
  }, [totalLeads, visibleLeads.length, selectedLeads.size, filters, currentView, currentLayout]);

  // Execute AI commands
  const executeCommand = useCallback(
    async (command: AILeadCommand): Promise<AICommandResult> => {

      try {
        switch (command.type) {
          case 'FILTER': {
            setFilters(command.filters as LeadFilterState);
            if (command.clearSelection) {
              setSelectedLeads(new Set());
            }
            return {
              commandId: command.id,
              success: true,
              metadata: { filters: command.filters },
            };
          }

          case 'SELECT': {
            const newSelection = new Set(
              command.mode === 'replace' ? command.leadIds : [...selectedLeads, ...command.leadIds]
            );
            setSelectedLeads(newSelection);
            return {
              commandId: command.id,
              success: true,
              affectedCount: command.leadIds.length,
            };
          }

          case 'SELECT_TOP': {
            // Sort visible leads by criteria
            const sorted = [...visibleLeads].sort((a, b) => {
              switch (command.criteria) {
                case 'value':
                  return (b.value || 0) - (a.value || 0);
                case 'score':
                  return (b.score || 0) - (a.score || 0);
                case 'date':
                  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                case 'priority':
                  return (b.priority || 0) - (a.priority || 0);
                default:
                  return 0;
              }
            });

            // Apply direction
            const finalList =
              command.direction === 'lowest' ? sorted.reverse() : sorted;

            // Select top N
            const topLeads = finalList.slice(0, command.count).map((lead) => lead.id);
            setSelectedLeads(new Set(topLeads));

            return {
              commandId: command.id,
              success: true,
              affectedCount: topLeads.length,
            };
          }

          case 'SELECT_ALL': {
            const allIds = visibleLeads.map((lead) => lead.id);
            setSelectedLeads(new Set(allIds));
            return {
              commandId: command.id,
              success: true,
              affectedCount: allIds.length,
            };
          }

          case 'CLEAR_SELECTION': {
            const previousCount = selectedLeads.size;
            setSelectedLeads(new Set());
            return {
              commandId: command.id,
              success: true,
              affectedCount: previousCount,
            };
          }

          case 'CHANGE_VIEW': {
            setCurrentView(command.view);
            return {
              commandId: command.id,
              success: true,
              metadata: { view: command.view },
            };
          }

          case 'CHANGE_LAYOUT': {
            setCurrentLayout(command.layout);
            return {
              commandId: command.id,
              success: true,
              metadata: { layout: command.layout },
            };
          }

          case 'SORT': {
            // This would be handled by LeadsList component
            return {
              commandId: command.id,
              success: true,
              metadata: { sortBy: command.sortBy, sortOrder: command.sortOrder },
            };
          }

          case 'BULK_ACTION': {
            // This would trigger the existing bulk action handlers
            return {
              commandId: command.id,
              success: true,
              metadata: { action: command.action, requiresConfirmation: command.requiresConfirmation },
            };
          }

          case 'HIGHLIGHT': {
            // Visual highlight would be handled by LeadsList
            return {
              commandId: command.id,
              success: true,
              affectedCount: command.leadIds.length,
            };
          }

          case 'NAVIGATE_TO_LEAD': {
            // Navigation would be handled by parent component
            return {
              commandId: command.id,
              success: true,
              metadata: { leadId: command.leadId },
            };
          }

          case 'SHOW_MESSAGE': {
            // Just a message, no action needed
            return {
              commandId: command.id,
              success: true,
            };
          }

          default:
            return {
              commandId: command.id,
              success: false,
              error: 'Unknown command type',
            };
        }
      } catch (error) {
        return {
          commandId: command.id,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [visibleLeads, selectedLeads, filters]
  );

  const value = useMemo(
    () => ({
      filters,
      selectedLeads,
      visibleLeads,
      currentView,
      currentLayout,
      totalLeads,
      viewMode,
      setFilters,
      setSelectedLeads,
      setVisibleLeads,
      setCurrentView,
      setCurrentLayout,
      setTotalLeads,
      setViewMode,
      executeCommand,
      getAIContext,
    }),
    [
      filters,
      selectedLeads,
      visibleLeads,
      currentView,
      currentLayout,
      totalLeads,
      viewMode,
      executeCommand,
      getAIContext,
    ]
  );

  return <LeadsPageContext.Provider value={value}>{children}</LeadsPageContext.Provider>;
};
